import {
  SAFE_ZONE_REGISTRY,
  safeZoneSupportsAspectRatio,
} from '@src/safe-zones/registry';
import { deepCloneSafe } from '@src/utility/deepCloneSafe';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable } from 'mobx';

import type {
  SafeZoneSelection,
  SafeZoneVariantKey,
  SafeZoneVariantState,
} from '@src/safe-zones/types';
import type { AssociatedVideo, Video, VideoClip } from '@src/types.ts/story';

/** Shape of the per-overlay variant flag map. */
export type SafeZoneVariantSnapshot = Partial<
  Record<`${SafeZoneSelection}.${SafeZoneVariantKey}`, boolean>
>;

const logger = LogManager.getLogger('ClipEditSessionStore');

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** The union type for `videoClipPreview` from VideoCreatorStore. */
export type VideoClipPreview = VideoClip & {
  autoPlay?: boolean;
  selectedDimension?: string;
  videoSource?: Video['videoSource'];
  transcriptionSnapshot?: Video['transcriptionSnapshot'];
  karaokeData?: Video['karaokeData'];
  // PLAT-10788 — per-language karaoke variants (populated on clip load).
  karaokeDataByLanguage?: Video['karaokeDataByLanguage'];
  scenes?: Video['scenes'];
  extraElementData?: Video['extraElementData'];
  activeVideoTemplateId?: string;
};

/** A single variant entry — either the parent clip or an associated video. */
export type ClipVariant = VideoClipPreview | AssociatedVideo;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Derive the aspect-ratio key used to index into the variants map.
 * Falls back to the provided default when the field is missing.
 */
function arKey(clip: { aspectRatio?: string }, fallback: string): string {
  return clip.aspectRatio || fallback;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export interface ClipEditSessionOptions {
  clip: VideoClipPreview;
  defaultAspectRatio: string;
  /**
   * Safe-zone overlay to restore on construction — from `?clipSafeZone=` when a
   * shared link carries one, else `ClipPreviewStore.lastSafeZoneSelection`
   * (singleton, so the choice survives modal close/reopen). Defaults to `'none'`,
   * and is dropped back to `'none'` when the clip's aspect ratio can't support it.
   */
  initialSafeZone?: SafeZoneSelection;
  /**
   * Variant flags for `initialSafeZone`, compound-keyed. **Pass explicit entries
   * whenever `initialSafeZone` is set**: the constructor assigns this map as-is
   * and never calls `selectSafeZone`, which is the only place `defaultVariants`
   * get seeded — so an empty map renders the overlay with every pill off.
   */
  initialSafeZoneVariants?: SafeZoneVariantSnapshot;
  /**
   * Transcript display language to restore on construction (`null` = Original).
   * Sourced from the `?clipLang=` URL param so a shared/refreshed link opens on
   * the same language (PLAT-10948), else from the clip's last-used language in
   * `ClipPreviewStore` (PLAT-11748). Defaults to Original when omitted.
   */
  initialLanguage?: string | null;
  /**
   * Preview mode to restore on construction. Sourced from the `?clipPlayback=`
   * URL param (PLAT-10948). Defaults to the published-aware auto value when
   * omitted.
   */
  initialPreviewMode?: 'live' | 'rendered';
  /**
   * A specific publish to play, from `?clipPublishRecord=` — so a link (or a
   * reopen) returns to the exact version picked in the Downloads list, not just
   * to the newest one (PLAT-11548). A stale id falls back to the automatic
   * rule; the selector treats "record gone" as no pin.
   */
  initialPublishRecordId?: string | null;
}

/**
 * MobX store that owns all local clip-editing state for the lifetime of the
 * `SocialClipShareModal`.
 *
 * Replaces the four-ownership-domain model (MobX store, MobX prop, React
 * state, adapter internal state) with a single observable owner that deep-
 * clones all data on construction.
 *
 * The `_variants` Map is the **single source of truth** for all variant data.
 * The parent variant's `associatedVideos` is kept in sync by
 * `_syncParentAssociatedVideos` whenever variants are added or replaced.
 */
export class ClipEditSessionStore {
  // ---- Primary data -------------------------------------------------------

  /** All variants keyed by aspect ratio. Includes the parent clip. */
  private _variants: Map<string, ClipVariant>;

  /** The AR key of the parent (original) clip in the variants map. */
  private _parentAr: string;

  // ---- UI state -----------------------------------------------------------
  private _selectedDimension: string;
  private _previewMode: 'live' | 'rendered';
  /**
   * Whether the preview mode was chosen rather than defaulted — a
   * `?clipPlayback=` URL seed or a press of the mode dropdown. Suppresses
   * `adoptPublishedRender`, so a shared link pinned to Edit Mode stays there.
   */
  private _previewModeExplicit: boolean;
  /**
   * The `PublishRecord` the user picked out of the Downloads list, so Published
   * Version plays *that* publish rather than the newest one for the language
   * (PLAT-11548). `null` = follow the automatic rule.
   */
  private _pinnedPublishRecordId: string | null;
  private _creatingDimension: string | null = null;
  private _selectedSafeZone: SafeZoneSelection;

  /**
   * The transcript display language selected in the clip-preview transcript
   * (`null` = Original). Owned here — rather than in the transcript bridge's
   * local React state — so the "Edit in Creator Studio" button can read the
   * current selection and carry it across the navigation (Creator Studio
   * restores language from the `?lang=` URL param). Lives for the modal's
   * lifetime; seeded from the `?clipLang=` URL param on open (PLAT-10948),
   * else Original.
   */
  private _selectedLanguage: string | null = null;

  /**
   * Per-overlay variant flags keyed by `${selection}.${variantKey}`. Scoped
   * per overlay so toggling LinkedIn's `showCaptions` doesn't bleed into
   * TikTok's. Replaced wholesale on every mutation (observable.ref).
   */
  private _safeZoneVariants: SafeZoneVariantSnapshot;

  constructor({
    clip,
    defaultAspectRatio,
    initialSafeZone,
    initialSafeZoneVariants,
    initialLanguage,
    initialPreviewMode,
    initialPublishRecordId,
  }: ClipEditSessionOptions) {
    // Deep-clone to sever all MobX reference chains (replaces `detachClip`).
    const clonedClip = deepCloneSafe(clip);

    this._selectedSafeZone = initialSafeZone ?? 'none';
    this._safeZoneVariants = initialSafeZoneVariants ?? {};
    this._selectedLanguage = initialLanguage ?? null;

    const mainAr = arKey(clonedClip, defaultAspectRatio);
    this._parentAr = mainAr;

    // Build variants map from the main clip + its associatedVideos.
    this._variants = new Map<string, ClipVariant>();
    this._variants.set(mainAr, clonedClip);

    for (const av of clonedClip.associatedVideos || []) {
      const avAr = arKey(av, defaultAspectRatio);
      if (!this._variants.has(avAr)) {
        this._variants.set(avAr, av);
      }
    }

    // Determine initial selected dimension.
    const firstPublished = this._allVariantsArray.find(
      v => !!v.videoFilePrimary,
    );
    const requestedDimension =
      clip.selectedDimension || clip.aspectRatio || defaultAspectRatio;
    this._previewMode =
      initialPreviewMode ?? (firstPublished ? 'rendered' : 'live');
    // PLAT-12133 — opening on Published Version for a dimension with no render
    // opens on the first published dimension instead. Decided here, before the
    // clip preview loads anything, so the editor loads that dimension's
    // timeline. Switching after mount moved only the selection, leaving the
    // editor on the other dimension's timeline, and the next save filed it
    // under the wrong aspect ratio.
    const requestedHasRender =
      !!this._variants.get(requestedDimension)?.videoFilePrimary;
    const initialDimension =
      this._previewMode === 'rendered' && !requestedHasRender && firstPublished
        ? arKey(firstPublished, defaultAspectRatio)
        : requestedDimension;
    this._selectedDimension = initialDimension;
    this._previewModeExplicit = !!initialPreviewMode;
    // A publish record belongs to one AR's Video record, so a pin cannot
    // survive opening on a different dimension (as in `selectDimension`).
    this._pinnedPublishRecordId =
      initialDimension === requestedDimension
        ? (initialPublishRecordId ?? null)
        : null;

    // An overlay only applies to the aspect ratios it was designed for. Until
    // now that rule lived solely in `selectDimension` (AR changed under a live
    // selection), which construction never calls — so a restored `?clipSafeZone`
    // paired with an incompatible `?clipId` could seed a state the dropdown
    // cannot produce (PLAT-10948). Same predicate, both entry points.
    if (
      !safeZoneSupportsAspectRatio(this._selectedSafeZone, initialDimension)
    ) {
      logger.info('Safe zone dropped: unsupported for the initial dimension', {
        selection: this._selectedSafeZone,
        initialDimension,
      });
      this._selectedSafeZone = 'none';
    }

    makeAutoObservable(
      this,
      {
        // Deep structure stored as ref — replaced wholesale, never patched.
        _variants: observable.ref,
        _safeZoneVariants: observable.ref,
      } as any,
      { autoBind: true },
    );

    logger.info('Session created', {
      mainAr,
      variantCount: this._variants.size,
      selectedDimension: this._selectedDimension,
      previewMode: this._previewMode,
    });
  }

  // ---- Computed -----------------------------------------------------------

  get selectedDimension(): string {
    return this._selectedDimension;
  }

  get previewMode(): 'live' | 'rendered' {
    return this._previewMode;
  }

  /** The publish the user picked from Downloads, or `null` for the newest. */
  get pinnedPublishRecordId(): string | null {
    return this._pinnedPublishRecordId;
  }

  get creatingDimension(): string | null {
    return this._creatingDimension;
  }

  get selectedSafeZone(): SafeZoneSelection {
    return this._selectedSafeZone;
  }

  /** Transcript display language for the clip preview (`null` = Original). */
  get selectedLanguage(): string | null {
    return this._selectedLanguage;
  }

  /**
   * Snapshot of the full per-overlay variant flag map. The modal mirrors
   * this back into `ClipPreviewStore.lastSafeZoneVariants` on every change
   * so the user's toggles survive modal close/reopen.
   */
  get safeZoneVariantsSnapshot(): SafeZoneVariantSnapshot {
    return this._safeZoneVariants;
  }

  /** Boolean state for one variant on the active overlay. */
  safeZoneVariant(key: SafeZoneVariantKey): boolean {
    if (this._selectedSafeZone === 'none') return false;
    return this._safeZoneVariants[`${this._selectedSafeZone}.${key}`] ?? false;
  }

  /** All variant flags for the active overlay as a snapshot object. */
  get safeZoneVariantState(): SafeZoneVariantState {
    if (this._selectedSafeZone === 'none') return {};
    const sel = this._selectedSafeZone;
    const out: SafeZoneVariantState = {};
    for (const compoundKey of Object.keys(this._safeZoneVariants)) {
      if (compoundKey.startsWith(`${sel}.`)) {
        const variant = compoundKey.slice(sel.length + 1) as SafeZoneVariantKey;
        out[variant] =
          this._safeZoneVariants[
            compoundKey as `${SafeZoneSelection}.${SafeZoneVariantKey}`
          ];
      }
    }
    return out;
  }

  get isCreatingVariant(): boolean {
    return this._creatingDimension !== null;
  }

  /**
   * The main (parent) clip.
   * Downstream consumers (e.g. `getAvailableClipFormats`,
   * `downloadVideoClip`, adapter's `loadVideoWithAspectRatio`) expect the
   * full `VideoClipPreview` shape with `associatedVideos`.
   *
   * The parent entry's `associatedVideos` is kept in sync by
   * `_syncParentAssociatedVideos` whenever variants are added/removed,
   * so this returns the map entry directly.
   */
  get mainClip(): VideoClipPreview {
    const parent = this._variants.get(this._parentAr);
    if (!parent) {
      // Should never happen — constructor always sets the parent.
      logger.error('mainClip: parent variant missing', { ar: this._parentAr });
      return {} as VideoClipPreview;
    }

    return parent as VideoClipPreview;
  }

  /**
   * The variant currently being edited / previewed.
   *
   * IMPORTANT: Must return the stored map entry directly — never a spread
   * copy. Consumers (e.g. VideoTemplatesContext, extraElementData utilities)
   * mutate fields like `extraElementData` in-place on this object. A copy
   * would silently discard those mutations.
   */
  get activeVariant(): ClipVariant {
    return (
      this._variants.get(this._selectedDimension) ??
      this._variants.get(this._parentAr)!
    );
  }

  /** Ordered list of all variants (parent + associated). */
  get allVariants(): Array<ClipVariant> {
    return Array.from(this._variants.values());
  }

  /** Whether at least one variant has a rendered file. */
  get hasAnyRenderedVersion(): boolean {
    return this.allVariants.some(v => !!v.videoFilePrimary);
  }

  /**
   * Find a variant by its persisted `id`. Returns `undefined` for unsaved
   * variants (no `id`) or when no match exists.
   */
  findVariantById(id: string): ClipVariant | undefined {
    for (const v of this._variants.values()) {
      if (v.id === id) return v;
    }
    return undefined;
  }

  /** Look up a variant by its aspect-ratio key. */
  findVariantByAr(ar: string): ClipVariant | undefined {
    return this._variants.get(ar);
  }

  // ---- Mutations (local only) ---------------------------------------------

  selectDimension(dimension: string): void {
    this._selectedDimension = dimension;
    // Publish records belong to one AR's Video record, so a pin cannot survive
    // the switch to a different format.
    this._pinnedPublishRecordId = null;
    // Auto-fall-back to 'none' if the active safe-zone selection doesn't
    // support the new aspect ratio. Auto-fallback never fires the analytics
    // event — only direct user changes via `selectSafeZone` do (the modal
    // handles analytics on the user-driven path).
    if (!safeZoneSupportsAspectRatio(this._selectedSafeZone, dimension)) {
      const previous = this._selectedSafeZone;
      this._selectedSafeZone = 'none';
      logger.info('selectDimension: safe-zone auto-fallback', {
        previousSelection: previous,
        newDimension: dimension,
      });
    }
  }

  selectSafeZone(selection: SafeZoneSelection): void {
    if (this._selectedSafeZone === selection) return;
    logger.info('selectSafeZone', {
      previousSelection: this._selectedSafeZone,
      newSelection: selection,
    });
    this._selectedSafeZone = selection;

    // Seed default variant flags the first time this overlay is selected in
    // the session. Skipped when the user already has any toggle history for
    // this overlay so re-selection doesn't clobber prior choices.
    if (selection !== 'none') {
      const definition = SAFE_ZONE_REGISTRY[selection];
      const defaults = definition?.defaultVariants;
      if (defaults) {
        const prefix = `${selection}.`;
        const hasExisting = Object.keys(this._safeZoneVariants).some(k =>
          k.startsWith(prefix),
        );
        if (!hasExisting) {
          const seeded: SafeZoneVariantSnapshot = {
            ...this._safeZoneVariants,
          };
          for (const [variantKey, value] of Object.entries(defaults)) {
            const compound =
              `${selection}.${variantKey}` as `${SafeZoneSelection}.${SafeZoneVariantKey}`;
            seeded[compound] = !!value;
          }
          this._safeZoneVariants = seeded;
        }
      }
    }
  }

  /**
   * Flip one variant flag on the active overlay. No-op when no overlay is
   * selected. Variant state is preserved across selection swaps so the user
   * gets back what they had if they re-pick the same overlay.
   */
  toggleSafeZoneVariant(key: SafeZoneVariantKey): void {
    if (this._selectedSafeZone === 'none') return;
    const compoundKey = `${this._selectedSafeZone}.${key}` as const;
    const next = !(this._safeZoneVariants[compoundKey] ?? false);
    this._safeZoneVariants = {
      ...this._safeZoneVariants,
      [compoundKey]: next,
    };
    logger.info('toggleSafeZoneVariant', {
      selection: this._selectedSafeZone,
      key,
      enabled: next,
    });
  }

  setPreviewMode(mode: 'live' | 'rendered'): void {
    this._previewMode = mode;
    this._previewModeExplicit = true;
  }

  /**
   * Open on the published render once the modal discovers one the constructor
   * couldn't see (PLAT-11548).
   *
   * Only the canonical (original-language) cell mirrors to `videoFilePrimary`,
   * so a clip published solely in a translation looks unpublished here — its
   * render is reachable only through its `PublishRecord`s, which arrive after
   * construction. This is the one-shot upgrade for that case, and it never
   * overrides a real choice: a `?clipPlayback=` seed or a press of the mode
   * dropdown marks the mode explicit and this becomes a no-op.
   */
  adoptPublishedRender(): void {
    if (this._previewModeExplicit || this._previewMode === 'rendered') return;
    this._previewMode = 'rendered';
    logger.info('Adopted published render as the opening preview mode');
  }

  /**
   * Play one specific publish from the Downloads list, and switch to Published
   * Version to show it. Marks the mode explicit — the user just said which
   * render they want to look at, so nothing may move them off it.
   */
  pinPublishRecord(recordId: string): void {
    this._pinnedPublishRecordId = recordId;
    this._previewMode = 'rendered';
    this._previewModeExplicit = true;
    logger.info('Pinned a publish record for playback', { recordId });
  }

  /** Set the clip-preview transcript display language (`null` = Original). */
  setSelectedLanguage(lang: string | null): void {
    this._selectedLanguage = lang;
    // A pinned publish belongs to one language; asking for another language is
    // asking to leave it, so the automatic rule takes over again.
    this._pinnedPublishRecordId = null;
  }

  setCreatingDimension(dimension: string | null): void {
    this._creatingDimension = dimension;
  }

  /**
   * Add a newly-created variant (e.g. from `loadVideoWithAspectRatio`).
   */
  addVariant(
    variant: AssociatedVideo & { activeVideoTemplateId?: string },
  ): void {
    const ar = arKey(variant, this._selectedDimension);
    const cloned = deepCloneSafe(variant);
    this._replaceVariant(ar, cloned);
    logger.info('addVariant', { ar, variantCount: this._variants.size });
  }

  /**
   * Partially update an existing variant's fields.
   * Used for resource loading, render status updates, thumbnail changes, etc.
   */
  updateVariant(ar: string, fields: Partial<ClipVariant>): void {
    const existing = this._variants.get(ar);
    if (!existing) {
      logger.warn('updateVariant: no variant for AR', { ar });
      return;
    }

    const updated = { ...existing, ...fields };
    this._replaceVariant(ar, updated);
  }

  /**
   * Update the thumbnail for a specific variant.
   */
  updateThumbnail(
    ar: string,
    thumbnail: { url: string },
    shareableImageId: string,
  ): void {
    this.updateVariant(ar, { thumbnail, shareableImageId });
  }

  /**
   * Wholesale replacement of a single variant's data. Used by undo/redo in
   * the adapter — `applySnapshot` restores an entire snapshot into the
   * active variant slot without partial merging.
   */
  replaceVariantData(ar: string, data: ClipVariant): void {
    const cloned = deepCloneSafe(data);
    this._replaceVariant(ar, cloned);
    logger.info('replaceVariantData', { ar });
  }

  /**
   * Replace the entire clip state. Used when the resource loader populates
   * the clip with fetched data (videoSource, transcription, etc.) and we
   * need to rebuild the session state.
   */
  replaceClip(clip: VideoClipPreview, defaultAspectRatio: string): void {
    const clonedClip = deepCloneSafe(clip);

    const mainAr = arKey(clonedClip, defaultAspectRatio);
    this._parentAr = mainAr;

    const newVariants = new Map<string, ClipVariant>();
    newVariants.set(mainAr, clonedClip);

    for (const av of clonedClip.associatedVideos || []) {
      const avAr = arKey(av, defaultAspectRatio);
      if (!newVariants.has(avAr)) {
        newVariants.set(avAr, av);
      }
    }

    this._variants = newVariants;

    logger.info('replaceClip', {
      mainAr,
      variantCount: this._variants.size,
    });
  }

  // ---- Disposal -----------------------------------------------------------

  dispose(): void {
    logger.info('Session disposed');
  }

  // ---- Private helpers ----------------------------------------------------

  private get _allVariantsArray(): Array<ClipVariant> {
    return Array.from(this._variants.values());
  }

  /**
   * Replace a variant in the map. Creates a new Map reference so MobX
   * detects the change (since `_variants` is `observable.ref`).
   */
  private _replaceVariant(ar: string, updated: ClipVariant): void {
    this._variants = new Map(this._variants);
    this._variants.set(ar, updated);
    this._syncParentAssociatedVideos();
  }

  /**
   * Rebuild the parent variant's `associatedVideos` array from the current
   * variants map. This keeps the parent entry's associatedVideos in sync
   * so that `activeVariant` and `mainClip` can return the map entry
   * directly (no spread copy), which is critical for in-place mutations
   * to fields like `extraElementData`.
   */
  private _syncParentAssociatedVideos(): void {
    const parent = this._variants.get(this._parentAr);
    if (!parent) return;

    const associatedVideos: AssociatedVideo[] = [];
    for (const [ar, v] of this._variants) {
      if (ar !== this._parentAr) {
        associatedVideos.push(v as AssociatedVideo);
      }
    }

    (parent as VideoClipPreview).associatedVideos = associatedVideos;
  }
}
